import {useState, useEffect, useLayoutEffect, useRef} from 'react'
import {Select, Button} from '@origamirisk/origami-storybook'
import {MenuItem, Box, Tabs, Tab, Typography, IconButton, Tooltip, Autocomplete, TextField} from '@mui/material'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import Editor, {DiffEditor as MonacoDiffEditor} from '@monaco-editor/react'
import {Version} from './types'
import {formatEffectiveDate} from '../../utils/dateFormatter'
import {fetchVersionFileAsJson} from '../../utils/versionFileApi'
import githubDarkTheme from '../../themes/github-dark.json'
import VersionChangeSummary from './VersionChangeSummary'

const defineGitHubDarkTheme = (monaco: any) => {
    monaco.editor.defineTheme('github-dark', githubDarkTheme as any)
    // Set default editor options
    monaco.editor.EditorOptions.fontSize.defaultValue = 12
}

// Safe wrapper for MonacoDiffEditor that handles cleanup properly
interface SafeDiffEditorProps {
    original: string
    modified: string
    language: string
    diffKey: string
    options?: any
}

function SafeDiffEditor({original, modified, language, diffKey, options}: SafeDiffEditorProps) {
    const [isReady, setIsReady] = useState(true)
    const [isMounted, setIsMounted] = useState(true)
    const editorRef = useRef<any>(null)
    const prevKeyRef = useRef<string>('')

    // Track mount state
    useEffect(() => {
        setIsMounted(true)
        return () => {
            setIsMounted(false)
        }
    }, [])

    // Cleanup on unmount - use useLayoutEffect for synchronous cleanup BEFORE the library's useEffect cleanup
    useLayoutEffect(() => {
        return () => {
            if (editorRef.current) {
                try {
                    // Clear models before the library tries to dispose them
                    const modifiedEditor = editorRef.current.getModifiedEditor?.()
                    const originalEditor = editorRef.current.getOriginalEditor?.()
                    if (modifiedEditor) modifiedEditor.setModel(null)
                    if (originalEditor) originalEditor.setModel(null)
                } catch (e) {
                    // Ignore disposal errors
                }
                editorRef.current = null
            }
        }
    }, [])

    // When key changes, briefly unmount to allow proper cleanup
    useEffect(() => {
        if (prevKeyRef.current && prevKeyRef.current !== diffKey) {
            setIsReady(false)
            const timer = setTimeout(() => {
                if (isMounted) setIsReady(true)
            }, 50)
            return () => clearTimeout(timer)
        }
        prevKeyRef.current = diffKey
    }, [diffKey, isMounted])

    const handleEditorDidMount = (editor: any) => {
        editorRef.current = editor
    }

    if (!isReady || !isMounted) {
        return (
            <Box sx={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography>Loading diff...</Typography>
            </Box>
        )
    }

    return (
        <MonacoDiffEditor
            key={diffKey}
            height="100%"
            language={language}
            original={original}
            modified={modified}
            onMount={handleEditorDidMount}
            options={{
                readOnly: true,
                minimap: {enabled: false},
                scrollBeyondLastLine: false,
                fontSize: 12,
                fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                wordWrap: 'off',
                automaticLayout: true,
                renderSideBySide: true,
                ignoreTrimWhitespace: true,
                diffAlgorithm: 'advanced',
                unicodeHighlight: {
                    ambiguousCharacters: false,
                    invisibleCharacters: false
                },
                ...options
            }}
            theme="github-dark"
            beforeMount={(monaco) => {
                defineGitHubDarkTheme(monaco)
            }}
        />
    )
}

function JsonView({data, placeholder}: { data: any, placeholder?: string }) {
    if (!data) {
        return <div style={{color: '#888', fontStyle: 'italic'}}>{placeholder ?? 'No data available'}</div>
    }
    return (
        <Box sx={{height: '100%', minHeight: '400px', border: '1px solid #e0e0e0'}}>
            <Editor
                height="100%"
                language="json"
                theme="github-dark"
                value={JSON.stringify(data, null, 2)}
                options={{
                    readOnly: true,
                    minimap: {enabled: false},
                    scrollBeyondLastLine: false,
                    fontSize: 12,
                    fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                    wordWrap: 'on',
                    lineNumbers: 'on',
                    folding: true,
                    automaticLayout: true
                }}
                beforeMount={(monaco) => {
                    defineGitHubDarkTheme(monaco)
                }}
            />
        </Box>
    )
}

interface DiffEditorProps {
    original: any
    modified: any
    diffKey?: string
}

function DiffEditor({original, modified, diffKey}: DiffEditorProps) {
    // Helper to sort object keys recursively for consistent comparison
    const sortKeys = (obj: any): any => {
        if (obj === null || typeof obj !== 'object') {
            return obj
        }

        if (Array.isArray(obj)) {
            // Recursively sort keys in array elements but preserve array order
            return obj.map(sortKeys)
        }

        // Sort object keys alphabetically
        return Object.keys(obj)
            .sort()
            .reduce((result: any, key) => {
                result[key] = sortKeys(obj[key])
                return result
            }, {})
    }

    const originalJson = JSON.stringify(sortKeys(original), null, 2)
    const modifiedJson = JSON.stringify(sortKeys(modified), null, 2)

    const originalLines = originalJson.split('\n')
    const modifiedLines = modifiedJson.split('\n')
    const isLargeFile = originalLines.length > 10000 || modifiedLines.length > 10000

    const currentKey = diffKey || `diff-${originalJson.length}-${modifiedJson.length}`

    return (
        <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            {isLargeFile && (
                <Box sx={{
                    p: 1.5,
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    mb: 1
                }}>
                    <Typography variant="body2" sx={{color: '#856404'}}>
                        ⚠️ Large file detected ({originalLines.length.toLocaleString()} lines).
                        Diff computation may be slow. Consider using search (Cmd/Ctrl+F) to find specific changes.
                    </Typography>
                </Box>
            )}
            <Box sx={{flex: 1, border: '1px solid #e0e0e0'}}>
                <SafeDiffEditor
                    diffKey={currentKey}
                    language="json"
                    original={originalJson}
                    modified={modifiedJson}
                    options={{
                        maxComputationTime: 0,
                        maxFileSize: 0
                    }}
                />
            </Box>
        </Box>
    )
}

interface RateTableViewerProps {
    rateTables: any
    compareRateTables?: any
    selectedTable?: string
    setSelectedTable?: (table: string) => void
}

interface SortConfig {
    column: string
    direction: 'asc' | 'desc'
}

function RateTableViewer({
                             rateTables,
                             compareRateTables,
                             selectedTable: controlledSelectedTable,
                             setSelectedTable: setControlledSelectedTable
                         }: RateTableViewerProps) {
    const [internalSelectedTable, setInternalSelectedTable] = useState<string>('')
    const [sortConfigs, setSortConfigs] = useState<SortConfig[]>([])
    const leftScrollRef = useRef<HTMLDivElement>(null)
    const rightScrollRef = useRef<HTMLDivElement>(null)

    // Use controlled prop if provided, otherwise use internal state
    const selectedTable = controlledSelectedTable !== undefined ? controlledSelectedTable : internalSelectedTable
    const setSelectedTable = setControlledSelectedTable || setInternalSelectedTable

    const isDiffMode = !!compareRateTables

    if (!rateTables || typeof rateTables !== 'object') {
        return <JsonView data={rateTables} placeholder="No rate tables data"/>
    }

    // Helper to detect the key column for sorting
    const getKeyColumn = (tableData: any): string | null => {
        if (!tableData?.headers) return null

        // Look for common key column names (case-insensitive)
        const keyPatterns = ['key', 'id', 'code', 'pk', 'primarykey']
        const keyHeader = tableData.headers.find((h: any) =>
            keyPatterns.some(pattern => h.Name.toLowerCase().includes(pattern))
        )

        // Return the key column name, or first column as fallback
        return keyHeader?.Name || tableData.headers[0]?.Name || null
    }

    // Helper to sort table rows by multiple columns
    const sortTableRows = (tableData: any, sortConfigs: SortConfig[]): any => {
        if (!tableData?.rows || !sortConfigs || sortConfigs.length === 0) return tableData

        const sortedRows = [...tableData.rows].sort((a, b) => {
            // Try each sort config in order until we find a difference
            for (const {column, direction} of sortConfigs) {
                const valA = a[column]
                const valB = b[column]

                let comparison = 0

                // Handle different data types
                if (typeof valA === 'number' && typeof valB === 'number') {
                    comparison = valA - valB
                } else {
                    // Convert to string for comparison
                    const strA = String(valA ?? '')
                    const strB = String(valB ?? '')
                    comparison = strA.localeCompare(strB)
                }

                // Apply direction
                if (comparison !== 0) {
                    return direction === 'asc' ? comparison : -comparison
                }
            }
            return 0
        })

        return {
            ...tableData,
            rows: sortedRows
        }
    }

    // Get all unique table names from both versions
    const allTableNames = isDiffMode && compareRateTables
        ? [...new Set([...Object.keys(rateTables), ...Object.keys(compareRateTables)])].sort()
        : Object.keys(rateTables).sort()

    const tableNames = allTableNames

    // Get raw selected table data
    const rawSelectedTableData = selectedTable ? rateTables[selectedTable] : null
    const rawCompareSelectedTableData = isDiffMode && selectedTable ? compareRateTables?.[selectedTable] : null

    // Initialize sort configs when table changes
    useEffect(() => {
        if (rawSelectedTableData) {
            const keyCol = getKeyColumn(rawSelectedTableData)
            if (keyCol) {
                setSortConfigs([{column: keyCol, direction: 'asc'}])
            } else {
                setSortConfigs([])
            }
        } else {
            setSortConfigs([])
        }
    }, [selectedTable])

    // Sort tables by sort configs
    const selectedTableData = rawSelectedTableData ? sortTableRows(rawSelectedTableData, sortConfigs) : rawSelectedTableData
    const compareSelectedTableData = rawCompareSelectedTableData ? sortTableRows(rawCompareSelectedTableData, sortConfigs) : rawCompareSelectedTableData

    // Helper to check if tables are different (used for display)
    const hasTableDiff = (table1: any, table2: any): boolean => {
        if (!table1 && !table2) return false
        if (!table1 || !table2) return true

        // Sort both tables before comparing using current sort configs
        const sorted1 = sortTableRows(table1, sortConfigs)
        const sorted2 = sortTableRows(table2, sortConfigs)

        return JSON.stringify(sorted1) !== JSON.stringify(sorted2)
    }

    // Helper to check if tables are different using auto-detected key column (for dropdown indicators)
    const hasTableDiffWithAutoSort = (table1: any, table2: any): boolean => {
        if (!table1 && !table2) return false
        if (!table1 || !table2) return true

        // Use auto-detected key column for consistent comparison
        const keyCol = getKeyColumn(table1)
        const autoSortConfigs = keyCol ? [{column: keyCol, direction: 'asc' as const}] : []

        const sorted1 = sortTableRows(table1, autoSortConfigs)
        const sorted2 = sortTableRows(table2, autoSortConfigs)

        return JSON.stringify(sorted1) !== JSON.stringify(sorted2)
    }

    const tableNamesWithDiffs = isDiffMode ? tableNames.filter(name =>
        hasTableDiffWithAutoSort(rateTables[name], compareRateTables?.[name])
    ) : []

    // Synchronized scrolling handler
    const handleScroll = (source: 'left' | 'right') => (e: React.UIEvent<HTMLDivElement>) => {
        const sourceElement = e.currentTarget
        const targetElement = source === 'left' ? rightScrollRef.current : leftScrollRef.current

        if (targetElement) {
            targetElement.scrollTop = sourceElement.scrollTop
            targetElement.scrollLeft = sourceElement.scrollLeft
        }
    }

    // Helper to compare row data
    const isRowDifferent = (row1: any, row2: any, headers: any[]): boolean => {
        return headers.some(header => {
            const val1 = row1?.[header.Name]
            const val2 = row2?.[header.Name]
            return JSON.stringify(val1) !== JSON.stringify(val2)
        })
    }

    // Helper to check if a specific cell is different
    const isCellDifferent = (row1: any, row2: any, headerName: string): boolean => {
        const val1 = row1?.[headerName]
        const val2 = row2?.[headerName]
        return JSON.stringify(val1) !== JSON.stringify(val2)
    }

    const renderTable = (tableData: any, compareData: any, title?: string, scrollRef?: React.RefObject<HTMLDivElement>, onScroll?: (e: React.UIEvent<HTMLDivElement>) => void) => {
        if (!tableData || !tableData.headers || !tableData.rows) {
            return <div style={{color: '#888', fontStyle: 'italic'}}>Invalid table structure</div>
        }

        const isComparing = !!compareData

        const periodIdDifferent = isComparing && tableData.rateTablePeriodId !== compareData?.rateTablePeriodId
        const tableIdDifferent = isComparing && tableData.rateTableId !== compareData?.rateTableId
        const versionKeyDifferent = isComparing &&
            tableData.resolvedVersionKey?.versionKeyNumber !== compareData?.resolvedVersionKey?.versionKeyNumber

        return (
            <Box ref={scrollRef} onScroll={onScroll} sx={{overflow: 'auto', maxHeight: '100%'}}>
                {title && <Typography variant="subtitle2" sx={{mb: 0.5, fontWeight: 'bold'}}>{title}</Typography>}
                <Typography variant="caption" sx={{display: 'block', mb: 1, color: '#666'}}>
                    <span>Version Key: </span>
                    {tableData.resolvedVersionKey ? (
                        <Tooltip
                            title={
                                <Box sx={{p: 0.5}}>
                                    <Typography variant="caption" sx={{display: 'block', fontWeight: 600}}>
                                        Resolved Version Key
                                    </Typography>
                                    <Typography variant="caption" sx={{display: 'block', mt: 0.5}}>
                                        ID: {tableData.resolvedVersionKey.versionKeyID}
                                    </Typography>
                                    <Typography variant="caption" sx={{display: 'block'}}>
                                        Number: {tableData.resolvedVersionKey.versionKeyNumber}
                                    </Typography>
                                    <Typography variant="caption" sx={{display: 'block'}}>
                                        Effective Date: {tableData.resolvedVersionKey.effectiveDate}
                                    </Typography>
                                    <Typography variant="caption" sx={{display: 'block'}}>
                                        Status: {tableData.resolvedVersionKey.status}
                                    </Typography>
                                    <Typography variant="caption" sx={{display: 'block'}}>
                                        Bureau: {tableData.resolvedVersionKey.bureau ? 'Yes' : 'No'}
                                    </Typography>
                                </Box>
                            }
                            placement="top"
                            arrow
                            enterDelay={500}
                        >
                            <span style={{
                                backgroundColor: versionKeyDifferent ? '#ffe0b2' : 'transparent',
                                padding: versionKeyDifferent ? '2px 4px' : '0',
                                fontWeight: versionKeyDifferent ? 600 : 400,
                                textDecoration: 'underline dotted',
                                textDecorationColor: '#999',
                                cursor: 'help'
                            }}>
                                {tableData.resolvedVersionKey.versionKeyNumber}
                            </span>
                        </Tooltip>
                    ) : (
                        <span className="client-override" />
                    )}
                </Typography>
                {(tableData.rateTablePeriodId || tableData.rateTableId) && (
                    <Typography variant="caption" sx={{display: 'block', mb: 1, color: '#666'}}>
                        <span>Rate Table Period ID: </span>
                        <span style={{
                            backgroundColor: periodIdDifferent ? '#ffe0b2' : 'transparent',
                            padding: periodIdDifferent ? '2px 4px' : '0',
                            fontWeight: periodIdDifferent ? 600 : 400
                        }}>
                            {tableData.rateTablePeriodId ?? 'N/A'}
                        </span>
                        <span> | Rate Table ID: </span>
                        <span style={{
                            backgroundColor: tableIdDifferent ? '#ffe0b2' : 'transparent',
                            padding: tableIdDifferent ? '2px 4px' : '0',
                            fontWeight: tableIdDifferent ? 600 : 400
                        }}>
                            {tableData.rateTableId ?? 'N/A'}
                        </span>
                    </Typography>
                )}
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    fontFamily: 'Menlo, Monaco, "Courier New", monospace'
                }}>
                    <thead>
                    <tr style={{backgroundColor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1}}>
                        {tableData.headers.map((header: any, idx: number) => (
                            <th key={idx} style={{
                                padding: '8px',
                                textAlign: 'left',
                                borderBottom: '2px solid #F5F5F5',
                                fontWeight: 600,
                                backgroundColor: '#f5f5f5'
                            }}>
                                {header.Name}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {tableData.rows.map((row: any, rowIdx: number) => {
                        const compareRow = compareData?.rows?.[rowIdx]
                        const rowDifferent = isComparing && compareRow && isRowDifferent(row, compareRow, tableData.headers)

                        return (
                            <tr key={rowIdx} style={{
                                backgroundColor: rowDifferent
                                    ? '#fff9e6'
                                    : (rowIdx % 2 === 0 ? 'white' : '#f9f9f9')
                            }}>
                                {tableData.headers.map((header: any, colIdx: number) => {
                                    const cellDifferent = isComparing && compareRow && isCellDifferent(row, compareRow, header.Name)

                                    return (
                                        <td key={colIdx} style={{
                                            padding: '6px 8px',
                                            borderBottom: '1px solid #eee',
                                            backgroundColor: cellDifferent ? '#ffe0b2' : 'transparent',
                                            fontWeight: cellDifferent ? 600 : 400
                                        }}>
                                            {row[header.Name] ?? ''}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                    </tbody>
                </table>
            </Box>
        )
    }

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, height: '100%'}}
             style={{marginTop: '15px', borderTop: '1px solid #ccc', paddingTop: '20px'}}>
            <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                <Autocomplete
                    value={selectedTable || null}
                    onChange={(_, newValue) => setSelectedTable(newValue || '')}
                    options={tableNames}
                    size="small"
                    sx={{
                        minWidth: 250,
                        '& .MuiInputBase-root': {
                            fontSize: '0.875rem',
                            padding: '0 8px',
                            height: '32px'
                        },
                        '& .MuiAutocomplete-input': {
                            padding: '4px 4px !important'
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Search tables..." size="small" />
                    )}
                    slotProps={{
                        popper: { placement: 'bottom-start' },
                        paper: { sx: { minWidth: '100%', width: 'max-content' } }
                    }}
                    ListboxProps={{
                        sx: {
                            '& .MuiAutocomplete-option': {
                                fontSize: '0.875rem',
                                padding: '4px 8px',
                                minHeight: 'auto',
                                whiteSpace: 'nowrap'
                            }
                        }
                    }}
                    renderOption={(props, option) => {
                        const hasDiff = tableNamesWithDiffs.includes(option)
                        return (
                            <li {...props} key={option} style={{fontSize: '0.875rem', padding: '4px 8px'}}>
                                {hasDiff &&
                                    <span style={{color: '#ff9800', marginRight: '6px', fontWeight: 'bold'}}>●</span>}
                                {option}
                            </li>
                        )
                    }}
                />

                {selectedTableData?.headers && (
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', pb: 1}}>
                        <Typography variant="caption" sx={{color: '#666', fontWeight: 600}}>Sort:</Typography>
                        {sortConfigs.map((config, index) => (
                            <Box key={index} sx={{
                                display: 'flex',
                                gap: 0.5,
                                alignItems: 'center',
                                backgroundColor: '#f5f5f5',
                                padding: '4px 8px',
                            }}>
                                {index > 0 &&
                                    <Typography variant="caption" sx={{color: '#999', mr: 0.5}}>then</Typography>}
                                <Select
                                    value={config.column}
                                    onChange={(e) => {
                                        const newConfigs = [...sortConfigs]
                                        newConfigs[index].column = e.target.value
                                        setSortConfigs(newConfigs)
                                    }}
                                    size="sm"
                                    sx={{minWidth: 120, fontSize: '12px', '& .MuiSelect-select': {py: 0.5}}}
                                >
                                    {selectedTableData.headers.map((header: any) => (
                                        <MenuItem key={header.Name} value={header.Name}>{header.Name}</MenuItem>
                                    ))}
                                </Select>
                                <Button
                                    variant="text"
                                    size="sm"
                                    onClick={() => {
                                        const newConfigs = [...sortConfigs]
                                        newConfigs[index].direction = config.direction === 'asc' ? 'desc' : 'asc'
                                        setSortConfigs(newConfigs)
                                    }}
                                    sx={{minWidth: 'auto', p: 0.5, fontSize: '12px'}}
                                >
                                    {config.direction === 'asc' ? '↑' : '↓'}
                                </Button>
                                <Button
                                    variant="text"
                                    size="sm"
                                    onClick={() => {
                                        setSortConfigs(sortConfigs.filter((_, i) => i !== index))
                                    }}
                                    sx={{minWidth: 'auto', p: 0.5, fontSize: '12px', color: '#999'}}
                                >
                                    ×
                                </Button>
                            </Box>
                        ))}
                        {sortConfigs.length < selectedTableData.headers.length && (
                            <a
                                onClick={() => {
                                    const usedColumns = new Set(sortConfigs.map(c => c.column))
                                    const availableColumn = selectedTableData.headers.find((h: any) => !usedColumns.has(h.Name))
                                    if (availableColumn) {
                                        setSortConfigs([...sortConfigs, {
                                            column: availableColumn.Name,
                                            direction: 'asc'
                                        }])
                                    }
                                }}
                                style={{fontSize: '12px'}}
                            >
                                + Add Sort Column
                            </a>
                        )}
                    </Box>
                )}
            </Box>

            {(selectedTableData || compareSelectedTableData) && (
                <Box sx={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                    {isDiffMode ? (
                        compareSelectedTableData && selectedTableData ? (
                            hasTableDiff(selectedTableData, compareSelectedTableData) ? (
                                <Box sx={{display: 'flex', gap: 2, flex: 1, overflow: 'hidden'}}>
                                    <Box sx={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                                        {renderTable(compareSelectedTableData, selectedTableData, 'Original', leftScrollRef, handleScroll('left'))}
                                    </Box>
                                    <Box sx={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                                        {renderTable(selectedTableData, compareSelectedTableData, 'Modified', rightScrollRef, handleScroll('right'))}
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    <Box className="status-bubble-added">
                                        <Typography variant="body2" sx={{color: '#2e7d32'}}>
                                            ✓ Tables are identical
                                        </Typography>
                                    </Box>
                                    <Box sx={{flex: 1, overflow: 'hidden'}}>
                                        {renderTable(selectedTableData, null)}
                                    </Box>
                                </>
                            )
                        ) : compareSelectedTableData ? (
                            // Table was removed (exists in comparison but not in current)
                            <Box sx={{display: 'flex', gap: 2, flex: 1, overflow: 'hidden'}}>
                                <Box sx={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                                    <Box className="status-bubble-removed">
                                        <Typography variant="body2" sx={{color: '#c62828', fontWeight: 600}}>
                                            ✗ Table Removed
                                        </Typography>
                                    </Box>
                                    {renderTable(compareSelectedTableData, null, 'Original (Removed)', leftScrollRef)}
                                </Box>
                                <Box sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#fafafa',
                                    border: '1px dashed #ccc'
                                }}>
                                    <Typography variant="body2" sx={{color: '#999', fontStyle: 'italic'}}>
                                        Table not present in current version
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            // Table was added (exists in current but not in comparison)
                            <Box sx={{display: 'flex', gap: 2, flex: 1, overflow: 'hidden'}}>
                                <Box sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#fafafa',
                                    border: '1px dashed #ccc'
                                }}>
                                    <Typography variant="body2" sx={{color: '#999', fontStyle: 'italic'}}>
                                        Table not present in comparison version
                                    </Typography>
                                </Box>
                                <Box sx={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                                    <Box className="status-bubble-added">
                                        <Typography variant="body2" sx={{color: '#2e7d32', fontWeight: 600}}>
                                            ✓ Table Added
                                        </Typography>
                                    </Box>
                                    {renderTable(selectedTableData, null, 'Current (Added)', rightScrollRef)}
                                </Box>
                            </Box>
                        )
                    ) : (
                        selectedTableData && renderTable(selectedTableData, null)
                    )}
                </Box>
            )}
        </Box>
    )
}

interface FormulaViewerProps {
    formulas: any
    compareFormulas?: any
    selectedDomain?: string
    setSelectedDomain?: (domain: string) => void
    selectedCoverage?: string
    setSelectedCoverage?: (coverage: string) => void
    selectedFormulaKey?: string
    setSelectedFormulaKey?: (key: string) => void
    formulaTabIndex?: number
    setFormulaTabIndex?: (index: number) => void
}

function FormulaViewer({
                           formulas,
                           compareFormulas,
                           selectedDomain: controlledDomain,
                           setSelectedDomain: setControlledDomain,
                           selectedCoverage: controlledCoverage,
                           setSelectedCoverage: setControlledCoverage,
                           selectedFormulaKey: controlledFormulaKey,
                           setSelectedFormulaKey: setControlledFormulaKey,
                           formulaTabIndex: controlledFormulaTabIndex,
                           setFormulaTabIndex: setControlledFormulaTabIndex
                       }: FormulaViewerProps) {
    const [internalSelectedDomain, setInternalSelectedDomain] = useState<string>('')
    const [internalSelectedCoverage, setInternalSelectedCoverage] = useState<string>('')
    const [internalSelectedFormulaKey, setInternalSelectedFormulaKey] = useState<string>('')
    const [internalFormulaTabIndex, setInternalFormulaTabIndex] = useState(0)

    // Use controlled props if provided, otherwise use internal state
    const selectedDomain = controlledDomain !== undefined ? controlledDomain : internalSelectedDomain
    const setSelectedDomain = setControlledDomain || setInternalSelectedDomain
    const selectedCoverage = controlledCoverage !== undefined ? controlledCoverage : internalSelectedCoverage
    const setSelectedCoverage = setControlledCoverage || setInternalSelectedCoverage
    const selectedFormulaKey = controlledFormulaKey !== undefined ? controlledFormulaKey : internalSelectedFormulaKey
    const setSelectedFormulaKey = setControlledFormulaKey || setInternalSelectedFormulaKey
    const formulaTabIndex = controlledFormulaTabIndex !== undefined ? controlledFormulaTabIndex : internalFormulaTabIndex
    const setFormulaTabIndex = setControlledFormulaTabIndex || setInternalFormulaTabIndex

    const isDiffMode = !!compareFormulas

    if (!formulas || typeof formulas !== 'object') {
        return <JsonView data={formulas} placeholder="No formulas data"/>
    }

    const domains = Object.keys(formulas).sort()
    const coverages = selectedDomain && formulas[selectedDomain] ? Object.keys(formulas[selectedDomain]).sort() : []
    const formulaKeys = selectedDomain && selectedCoverage && formulas[selectedDomain]?.[selectedCoverage]
        ? Object.keys(formulas[selectedDomain][selectedCoverage]).sort()
        : []

    const selectedFormula = selectedDomain && selectedCoverage && selectedFormulaKey
        ? formulas[selectedDomain]?.[selectedCoverage]?.[selectedFormulaKey]
        : null

    const compareSelectedFormula = isDiffMode && selectedDomain && selectedCoverage && selectedFormulaKey
        ? compareFormulas?.[selectedDomain]?.[selectedCoverage]?.[selectedFormulaKey]
        : null

    const isExpression = selectedFormula?.ColumnType === 'E'
    const isBasicMath = selectedFormula?.ColumnType === 'Q'

    // Helper function to check if two formulas are different
    const hasFormulaDiff = (formula1: any, formula2: any): boolean => {
        if (!formula1 && !formula2) return false
        if (!formula1 || !formula2) return true
        return JSON.stringify(formula1) !== JSON.stringify(formula2)
    }

    // Calculate which domains have diffs
    const domainsWithDiffs = isDiffMode ? domains.filter(domain => {
        const domainFormulas = formulas[domain]
        const compareDomainFormulas = compareFormulas?.[domain]
        if (!domainFormulas && !compareDomainFormulas) return false
        if (!domainFormulas || !compareDomainFormulas) return true

        return Object.keys(domainFormulas).some(coverage => {
            const coverageFormulas = domainFormulas[coverage]
            const compareCoverageFormulas = compareDomainFormulas[coverage]
            if (!coverageFormulas && !compareCoverageFormulas) return false
            if (!coverageFormulas || !compareCoverageFormulas) return true

            return Object.keys(coverageFormulas).some(key =>
                hasFormulaDiff(coverageFormulas[key], compareCoverageFormulas[key])
            )
        })
    }) : []

    // Calculate which coverages have diffs for selected domain
    const coveragesWithDiffs = isDiffMode && selectedDomain ? coverages.filter(coverage => {
        const coverageFormulas = formulas[selectedDomain]?.[coverage]
        const compareCoverageFormulas = compareFormulas?.[selectedDomain]?.[coverage]
        if (!coverageFormulas && !compareCoverageFormulas) return false
        if (!coverageFormulas || !compareCoverageFormulas) return true

        return Object.keys(coverageFormulas).some(key =>
            hasFormulaDiff(coverageFormulas[key], compareCoverageFormulas[key])
        )
    }) : []

    // Calculate which formula keys have diffs for selected domain/coverage
    const formulaKeysWithDiffs = isDiffMode && selectedDomain && selectedCoverage ? formulaKeys.filter(key => {
        const formula = formulas[selectedDomain]?.[selectedCoverage]?.[key]
        const compareFormula = compareFormulas?.[selectedDomain]?.[selectedCoverage]?.[key]
        return hasFormulaDiff(formula, compareFormula)
    }) : []

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, height: '100%'}}>
            <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                <Autocomplete
                    value={selectedDomain || null}
                    onChange={(_, newValue) => {
                        setSelectedDomain(newValue || '')
                        setSelectedCoverage('')
                        setSelectedFormulaKey('')
                    }}
                    options={domains}
                    size="small"
                    sx={{
                        minWidth: 150,
                        '& .MuiInputBase-root': {
                            fontSize: '0.875rem',
                            padding: '0 8px',
                            height: '32px'
                        },
                        '& .MuiAutocomplete-input': {
                            padding: '4px 4px !important'
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Search domains..." size="small" />
                    )}
                    slotProps={{
                        popper: { placement: 'bottom-start' },
                        paper: { sx: { minWidth: '100%', width: 'max-content' } }
                    }}
                    ListboxProps={{
                        sx: {
                            '& .MuiAutocomplete-option': {
                                fontSize: '0.875rem',
                                padding: '4px 8px',
                                minHeight: 'auto',
                                whiteSpace: 'nowrap'
                            }
                        }
                    }}
                    renderOption={(props, option) => {
                        const hasDiff = domainsWithDiffs.includes(option)
                        return (
                            <li {...props} key={option} style={{fontSize: '0.875rem', padding: '4px 8px'}}>
                                {hasDiff &&
                                    <span style={{color: '#ff9800', marginRight: '6px', fontWeight: 'bold'}}>●</span>}
                                {option}
                            </li>
                        )
                    }}
                />
                {selectedDomain && (
                    <Autocomplete
                        value={selectedCoverage || null}
                        onChange={(_, newValue) => {
                            setSelectedCoverage(newValue || '')
                            setSelectedFormulaKey('')
                        }}
                        options={coverages}
                        size="small"
                        sx={{
                            minWidth: 150,
                            '& .MuiInputBase-root': {
                                fontSize: '0.875rem',
                                padding: '0 8px',
                                height: '32px'
                            },
                            '& .MuiAutocomplete-input': {
                                padding: '4px 4px !important'
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Search coverages..." size="small" />
                        )}
                        slotProps={{
                            popper: { placement: 'bottom-start' },
                            paper: { sx: { minWidth: '100%', width: 'max-content' } }
                        }}
                        ListboxProps={{
                            sx: {
                                '& .MuiAutocomplete-option': {
                                    fontSize: '0.875rem',
                                    padding: '4px 8px',
                                    minHeight: 'auto',
                                    whiteSpace: 'nowrap'
                                }
                            }
                        }}
                        renderOption={(props, option) => {
                            const hasDiff = coveragesWithDiffs.includes(option)
                            return (
                                <li {...props} key={option} style={{fontSize: '0.875rem', padding: '4px 8px'}}>
                                    {hasDiff &&
                                        <span style={{color: '#ff9800', marginRight: '6px', fontWeight: 'bold'}}>●</span>}
                                    {option}
                                </li>
                            )
                        }}
                    />
                )}
                {selectedCoverage && (
                    <Autocomplete
                        value={selectedFormulaKey || null}
                        onChange={(_, newValue) => {
                            setSelectedFormulaKey(newValue || '')
                            const formula = formulas[selectedDomain]?.[selectedCoverage]?.[newValue || '']
                            const columnType = formula?.ColumnType
                            setFormulaTabIndex((columnType === 'E' || columnType === 'Q') ? 1 : 0)
                        }}
                        options={formulaKeys}
                        size="small"
                        sx={{
                            minWidth: 200,
                            '& .MuiInputBase-root': {
                                fontSize: '0.875rem',
                                padding: '0 8px',
                                height: '32px'
                            },
                            '& .MuiAutocomplete-input': {
                                padding: '4px 4px !important'
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Search formulas..." size="small" />
                        )}
                        slotProps={{
                            popper: { placement: 'bottom-start' },
                            paper: { sx: { minWidth: '100%', width: 'max-content' } }
                        }}
                        ListboxProps={{
                            sx: {
                                '& .MuiAutocomplete-option': {
                                    fontSize: '0.875rem',
                                    padding: '4px 8px',
                                    minHeight: 'auto',
                                    whiteSpace: 'nowrap'
                                }
                            }
                        }}
                        getOptionLabel={(key) => {
                            const formula = formulas[selectedDomain]?.[selectedCoverage]?.[key]
                            return formula?.FieldName || key
                        }}
                        renderOption={(props, key) => {
                            const formula = formulas[selectedDomain][selectedCoverage][key]
                            const hasDiff = formulaKeysWithDiffs.includes(key)
                            return (
                                <li {...props} key={key} style={{fontSize: '0.875rem', padding: '4px 8px'}}>
                                    {hasDiff &&
                                        <span style={{color: '#ff9800', marginRight: '6px', fontWeight: 'bold'}}>●</span>}
                                    {formula?.FieldName || key}
                                </li>
                            )
                        }}
                    />
                )}
            </Box>

            {(selectedFormula || (isDiffMode && compareSelectedFormula)) && (
                <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                    {selectedFormula && isDiffMode && compareSelectedFormula ? (
                        // Diff mode with both formulas: show split header with version keys for both sides
                        (() => {
                            const versionKeyDifferent = selectedFormula.ResolvedVersionKey?.versionKeyNumber !== compareSelectedFormula.ResolvedVersionKey?.versionKeyNumber
                            return (
                                <Box sx={{display: 'flex', gap: 2, mb: 1}}>
                                    <Box sx={{flex: 1}}>
                                        <Typography variant="caption" sx={{display: 'block', color: '#666'}}>
                                            <span>Original Version Key: </span>
                                            {compareSelectedFormula.ResolvedVersionKey ? (
                                                <Tooltip
                                                    title={
                                                        <Box sx={{p: 0.5}}>
                                                            <Typography variant="caption"
                                                                        sx={{display: 'block', fontWeight: 600}}>
                                                                Resolved Version Key
                                                            </Typography>
                                                            <Typography variant="caption"
                                                                        sx={{display: 'block', mt: 0.5}}>
                                                                ID: {compareSelectedFormula.ResolvedVersionKey.versionKeyID}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                                Number: {compareSelectedFormula.ResolvedVersionKey.versionKeyNumber}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                                Effective
                                                                Date: {compareSelectedFormula.ResolvedVersionKey.effectiveDate}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                                Status: {compareSelectedFormula.ResolvedVersionKey.status}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                                Bureau: {compareSelectedFormula.ResolvedVersionKey.bureau ? 'Yes' : 'No'}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    placement="top"
                                                    arrow
                                                >
                                                    <span style={{
                                                        backgroundColor: versionKeyDifferent ? '#ffe0b2' : 'transparent',
                                                        padding: versionKeyDifferent ? '2px 4px' : '0',
                                                        fontWeight: versionKeyDifferent ? 600 : 400,
                                                        textDecoration: 'underline dotted',
                                                        textDecorationColor: '#999',
                                                        cursor: 'help'
                                                    }}>
                                                        {compareSelectedFormula.ResolvedVersionKey.versionKeyNumber}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <span className="client-override" />
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box sx={{flex: 1}}>
                                        <Typography variant="caption" sx={{display: 'block', color: '#666'}}>
                                            <span>Modified Version Key: </span>
                                            {selectedFormula.ResolvedVersionKey ? (
                                                <Tooltip
                                                    title={
                                                        <Box sx={{p: 0.5}}>
                                                            <Typography variant="caption"
                                                                        sx={{display: 'block', fontWeight: 600}}>
                                                                Resolved Version Key
                                                            </Typography>
                                                            <Typography variant="caption"
                                                                        sx={{display: 'block', mt: 0.5}}>
                                                                ID: {selectedFormula.ResolvedVersionKey.versionKeyID}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                                Number: {selectedFormula.ResolvedVersionKey.versionKeyNumber}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                                Effective
                                                                Date: {selectedFormula.ResolvedVersionKey.effectiveDate}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                                Status: {selectedFormula.ResolvedVersionKey.status}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                                Bureau: {selectedFormula.ResolvedVersionKey.bureau ? 'Yes' : 'No'}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    placement="top"
                                                    arrow
                                                >
                                                    <span style={{
                                                        backgroundColor: versionKeyDifferent ? '#ffe0b2' : 'transparent',
                                                        padding: versionKeyDifferent ? '2px 4px' : '0',
                                                        fontWeight: versionKeyDifferent ? 600 : 400,
                                                        textDecoration: 'underline dotted',
                                                        textDecorationColor: '#999',
                                                        cursor: 'help'
                                                    }}>
                                                        {selectedFormula.ResolvedVersionKey.versionKeyNumber}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <span className="client-override" />
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            )
                        })()
                    ) : isDiffMode && !compareSelectedFormula && selectedFormula ? (
                        // Formula was added (exists in current but not in comparison)
                        <Typography variant="caption" sx={{display: 'block', mb: 1, color: '#666'}}>
                            <span>Added Version Key: </span>
                            {selectedFormula.ResolvedVersionKey ? (
                                <Tooltip
                                    title={
                                        <Box sx={{p: 0.5}}>
                                            <Typography variant="caption" sx={{display: 'block', fontWeight: 600}}>
                                                Resolved Version Key
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block', mt: 0.5}}>
                                                ID: {selectedFormula.ResolvedVersionKey.versionKeyID}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Number: {selectedFormula.ResolvedVersionKey.versionKeyNumber}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Effective Date: {selectedFormula.ResolvedVersionKey.effectiveDate}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Status: {selectedFormula.ResolvedVersionKey.status}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Bureau: {selectedFormula.ResolvedVersionKey.bureau ? 'Yes' : 'No'}
                                            </Typography>
                                        </Box>
                                    }
                                    placement="top"
                                    arrow
                                >
                                    <span style={{
                                        textDecoration: 'underline dotted',
                                        textDecorationColor: '#999',
                                        cursor: 'help'
                                    }}>
                                        {selectedFormula.ResolvedVersionKey.versionKeyNumber}
                                    </span>
                                </Tooltip>
                            ) : (
                                <span className="client-override" />
                            )}
                        </Typography>
                    ) : isDiffMode && compareSelectedFormula && !selectedFormula ? (
                        // Formula was removed (exists in comparison but not in current)
                        <Typography variant="caption" sx={{display: 'block', mb: 1, color: '#666'}}>
                            <span>Removed Version Key: </span>
                            {compareSelectedFormula.ResolvedVersionKey ? (
                                <Tooltip
                                    title={
                                        <Box sx={{p: 0.5}}>
                                            <Typography variant="caption" sx={{display: 'block', fontWeight: 600}}>
                                                Resolved Version Key
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block', mt: 0.5}}>
                                                ID: {compareSelectedFormula.ResolvedVersionKey.versionKeyID}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Number: {compareSelectedFormula.ResolvedVersionKey.versionKeyNumber}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Effective Date: {compareSelectedFormula.ResolvedVersionKey.effectiveDate}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Status: {compareSelectedFormula.ResolvedVersionKey.status}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Bureau: {compareSelectedFormula.ResolvedVersionKey.bureau ? 'Yes' : 'No'}
                                            </Typography>
                                        </Box>
                                    }
                                    placement="top"
                                    arrow
                                >
                                    <span style={{
                                        textDecoration: 'underline dotted',
                                        textDecorationColor: '#999',
                                        cursor: 'help'
                                    }}>
                                        {compareSelectedFormula.ResolvedVersionKey.versionKeyNumber}
                                    </span>
                                </Tooltip>
                            ) : (
                                <span className="client-override" />
                            )}
                        </Typography>
                    ) : isDiffMode && !selectedFormula && compareSelectedFormula ? (
                        // Formula was removed: show split header with version keys for both sides
                        <Box sx={{display: 'flex', gap: 2, mb: 1}}>
                            <Box sx={{flex: 1}}>
                                <Typography variant="subtitle2" sx={{fontWeight: 'bold', mb: 0.5}}>Original</Typography>
                                <Typography variant="caption" sx={{display: 'block', color: '#666'}}>
                                    <span>Version Key: </span>
                                    {compareSelectedFormula.ResolvedVersionKey ? (
                                        <Tooltip
                                            title={
                                                <Box sx={{p: 0.5}}>
                                                    <Typography variant="caption" sx={{display: 'block', fontWeight: 600}}>
                                                        Resolved Version Key
                                                    </Typography>
                                                    <Typography variant="caption" sx={{display: 'block', mt: 0.5}}>
                                                        ID: {compareSelectedFormula.ResolvedVersionKey.versionKeyID}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{display: 'block'}}>
                                                        Number: {compareSelectedFormula.ResolvedVersionKey.versionKeyNumber}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{display: 'block'}}>
                                                        Effective Date: {compareSelectedFormula.ResolvedVersionKey.effectiveDate}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{display: 'block'}}>
                                                        Status: {compareSelectedFormula.ResolvedVersionKey.status}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{display: 'block'}}>
                                                        Bureau: {compareSelectedFormula.ResolvedVersionKey.bureau ? 'Yes' : 'No'}
                                                    </Typography>
                                                </Box>
                                            }
                                            placement="top"
                                            arrow
                                            enterDelay={500}
                                        >
                                            <span style={{
                                                textDecoration: 'underline dotted',
                                                textDecorationColor: '#999',
                                                cursor: 'help'
                                            }}>
                                                {compareSelectedFormula.ResolvedVersionKey.versionKeyNumber}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <span className="client-override" />
                                    )}
                                </Typography>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="subtitle2" sx={{fontWeight: 'bold', mb: 0.5}}>Modified</Typography>
                                <Typography variant="caption" sx={{display: 'block', color: '#666'}}>
                                    <span style={{visibility: 'hidden'}}>Version Key: </span>
                                </Typography>
                            </Box>
                        </Box>
                    ) : selectedFormula ? (
                        // Non-diff mode: show single version key
                        <Typography variant="caption" sx={{display: 'block', mb: 1, color: '#666'}}>
                            <span>Version Key: </span>
                            {selectedFormula.ResolvedVersionKey ? (
                                <Tooltip
                                    title={
                                        <Box sx={{p: 0.5}}>
                                            <Typography variant="caption" sx={{display: 'block', fontWeight: 600}}>
                                                Resolved Version Key
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block', mt: 0.5}}>
                                                ID: {selectedFormula.ResolvedVersionKey.versionKeyID}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Number: {selectedFormula.ResolvedVersionKey.versionKeyNumber}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Effective Date: {selectedFormula.ResolvedVersionKey.effectiveDate}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Status: {selectedFormula.ResolvedVersionKey.status}
                                            </Typography>
                                            <Typography variant="caption" sx={{display: 'block'}}>
                                                Bureau: {selectedFormula.ResolvedVersionKey.bureau ? 'Yes' : 'No'}
                                            </Typography>
                                        </Box>
                                    }
                                    placement="top"
                                    arrow
                                    enterDelay={500}
                                >
                                    <span style={{
                                        textDecoration: 'underline dotted',
                                        textDecorationColor: '#999',
                                        cursor: 'help'
                                    }}>
                                        {selectedFormula.ResolvedVersionKey.versionKeyNumber}
                                    </span>
                                </Tooltip>
                            ) : (
                                <span className="client-override" />
                            )}
                        </Typography>
                    ) : null}
                    <Box sx={{borderBottom: 0, borderColor: 'divider', pb: '2px', backgroundColor: '#F5F5F5'}}>
                        <Tabs
                            value={formulaTabIndex}
                            onChange={(_, v) => setFormulaTabIndex(v)}
                            sx={{minHeight: 36}}
                        >
                            <Tab label="Raw" sx={{minHeight: 36, py: 0}}/>
                            {isExpression && <Tab label="Expression" sx={{minHeight: 36, py: 0}}/>}
                            {isBasicMath && <Tab label="Basic Math Formula" sx={{minHeight: 36, py: 0}}/>}
                        </Tabs>
                    </Box>
                    <Box sx={{flex: 1, overflow: 'auto', pt: 0}}>
                        {formulaTabIndex === 0 && (
                            isDiffMode && selectedFormula ? (
                                compareSelectedFormula ? (
                                    <DiffEditor
                                        key={`json-diff-${selectedDomain}-${selectedCoverage}-${selectedFormulaKey}-${formulaTabIndex}`}
                                        diffKey={`json-diff-${selectedDomain}-${selectedCoverage}-${selectedFormulaKey}-${formulaTabIndex}`}
                                        original={compareSelectedFormula}
                                        modified={selectedFormula}
                                    />
                                ) : (
                                    // Formula was added (exists in current but not in comparison)
                                    <Box sx={{display: 'flex', gap: 2, height: '100%'}}>
                                        <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                            <Typography variant="subtitle2"
                                                        sx={{mb: 0.5, fontWeight: 'bold'}}>Original</Typography>
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#fafafa',
                                                border: '1px dashed #ccc'
                                            }}>
                                                <Typography variant="body2" sx={{color: '#999', fontStyle: 'italic'}}>
                                                    Formula not present in comparison version
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden'
                                        }}>
                                            <Typography variant="subtitle2"
                                                        sx={{mb: 0.5, fontWeight: 'bold'}}>Modified</Typography>
                                            <Box className="status-bubble-added">
                                                <Typography variant="body2" sx={{color: '#2e7d32', fontWeight: 600}}>
                                                    ✓ Formula Added
                                                </Typography>
                                            </Box>
                                            <Box sx={{flex: 1, overflow: 'auto'}}>
                                                <JsonView data={selectedFormula} placeholder="No formula"/>
                                            </Box>
                                        </Box>
                                    </Box>
                                )
                            ) : !isDiffMode && selectedFormula ? (
                                <JsonView data={selectedFormula} placeholder="No formula"/>
                            ) : !selectedFormula && isDiffMode && compareSelectedFormula ? (
                                // Formula was removed (exists in comparison but not in current)
                                <Box sx={{display: 'flex', gap: 2, height: '100%'}}>
                                    <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                                        <Box className="status-bubble-removed">
                                            <Typography variant="body2" sx={{color: '#c62828', fontWeight: 600}}>
                                                ✗ Formula Removed
                                            </Typography>
                                        </Box>
                                        <Box sx={{flex: 1, overflow: 'auto'}}>
                                            <JsonView data={compareSelectedFormula} placeholder="No formula"/>
                                        </Box>
                                    </Box>
                                    <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                        <Box sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#fafafa',
                                            border: '1px dashed #ccc'
                                        }}>
                                            <Typography variant="body2" sx={{color: '#999', fontStyle: 'italic'}}>
                                                Formula not present in current version
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : null
                        )}
                        {formulaTabIndex === 1 && isExpression && (
                            isDiffMode && selectedFormula ? (
                                compareSelectedFormula?.Expression ? (
                                    <Box sx={{height: '100%', border: '1px solid #e0e0e0'}}>
                                        <SafeDiffEditor
                                            diffKey={`expr-diff-${selectedDomain}-${selectedCoverage}-${selectedFormulaKey}-${formulaTabIndex}`}
                                            language="javascript"
                                            original={compareSelectedFormula.Expression || ''}
                                            modified={selectedFormula.Expression || ''}
                                            options={{
                                                lineNumbers: 'on',
                                                folding: true
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    // Formula was added (exists in current but not in comparison)
                                    <Box sx={{display: 'flex', gap: 2, height: '100%'}}>
                                        <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                            {/*<Typography variant="subtitle2" sx={{mb: 0.5, fontWeight: 'bold'}}>Original e4</Typography>*/}
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#fafafa',
                                                border: '1px dashed #ccc'
                                            }}>
                                                <Typography variant="body2" sx={{color: '#999', fontStyle: 'italic'}}>
                                                    Formula not present in comparison version
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden'
                                        }}>
                                            {/*<Typography variant="subtitle2" sx={{mb: 0.5, fontWeight: 'bold'}}>Modified</Typography>*/}
                                            <Box className="status-bubble-added">
                                                <Typography variant="body2" sx={{color: '#2e7d32', fontWeight: 600}}>
                                                    ✓ Formula Added
                                                </Typography>
                                            </Box>
                                            <Box sx={{flex: 1, overflow: 'hidden'}}>
                                                <Editor
                                                    key={`expr-single-${selectedDomain}-${selectedCoverage}-${selectedFormulaKey}-${formulaTabIndex}`}
                                                    height="100%"
                                                    language="javascript"
                                                    theme="github-dark"
                                                    value={selectedFormula.Expression}
                                                    options={{
                                                        readOnly: true,
                                                        minimap: {enabled: false},
                                                        scrollBeyondLastLine: false,
                                                        fontSize: 12,
                                                        fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                                                        wordWrap: 'on',
                                                        lineNumbers: 'on',
                                                        folding: true,
                                                        automaticLayout: true
                                                    }}
                                                    beforeMount={(monaco) => {
                                                        defineGitHubDarkTheme(monaco)
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                )
                            ) : !isDiffMode && selectedFormula ? (
                                <Editor
                                    key={`expr-view-${selectedDomain}-${selectedCoverage}-${selectedFormulaKey}-${formulaTabIndex}`}
                                    height="100%"
                                    language="javascript"
                                    theme="github-dark"
                                    value={selectedFormula.Expression}
                                    options={{
                                        readOnly: true,
                                        minimap: {enabled: false},
                                        scrollBeyondLastLine: false,
                                        fontSize: 12,
                                        fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                                        wordWrap: 'on',
                                        lineNumbers: 'on',
                                        folding: true,
                                        automaticLayout: true
                                    }}
                                    beforeMount={(monaco) => {
                                        defineGitHubDarkTheme(monaco)
                                    }}
                                />
                            ) : !selectedFormula && isDiffMode && compareSelectedFormula?.Expression ? (
                                // Formula was removed (exists in comparison but not in current)
                                <Box sx={{display: 'flex', gap: 2, height: '100%'}}>
                                    <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                                        <Box className="status-bubble-removed">
                                            <Typography variant="body2" sx={{color: '#c62828', fontWeight: 600}}>
                                                ✗ Formula Removed
                                            </Typography>
                                        </Box>
                                        <Box sx={{flex: 1, overflow: 'hidden'}}>
                                            <Editor
                                                key={`expr-removed-${selectedDomain}-${selectedCoverage}-${selectedFormulaKey}-${formulaTabIndex}`}
                                                height="100%"
                                                language="javascript"
                                                theme="github-dark"
                                                value={compareSelectedFormula.Expression}
                                                options={{
                                                    readOnly: true,
                                                    minimap: {enabled: false},
                                                    scrollBeyondLastLine: false,
                                                    fontSize: 12,
                                                    fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                                                    wordWrap: 'on',
                                                    lineNumbers: 'on',
                                                    folding: true,
                                                    automaticLayout: true
                                                }}
                                                beforeMount={(monaco) => {
                                                    defineGitHubDarkTheme(monaco)
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                        <Box sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#fafafa',
                                            border: '1px dashed #ccc'
                                        }}>
                                            <Typography variant="body2" sx={{color: '#999', fontStyle: 'italic'}}>
                                                Formula not present in current version
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : null
                        )}
                        {formulaTabIndex === 1 && isBasicMath && (
                            isDiffMode && selectedFormula ? (
                                compareSelectedFormula?.BasicMathFormula ? (
                                    <Box sx={{display: 'flex', gap: 2, height: '100%'}}>
                                        <Box sx={{
                                            flex: 1,
                                            p: 2,
                                            background: '#1e1e1e',
                                            fontFamily: 'monospace',
                                            fontSize: '14px',
                                            lineHeight: 1.8,
                                            overflow: 'auto'
                                        }}>
                                            <div style={{
                                                color: '#c62828',
                                                marginBottom: '8px',
                                                fontWeight: 'bold'
                                            }}>Original:
                                            </div>
                                            {compareSelectedFormula.BasicMathFormula?.split(/(\{[^}]+\})/).map((part: string, i: number) => (
                                                part.startsWith('{') && part.endsWith('}') ? (
                                                    <span key={i} style={{
                                                        color: '#9cdcfe',
                                                        background: '#2d2d2d',
                                                        padding: '2px 6px',
                                                        margin: '0 2px'
                                                    }}>
                                                        {part}
                                                    </span>
                                                ) : (
                                                    <span key={i} style={{color: '#d4d4d4'}}>{part}</span>
                                                )
                                            ))}
                                        </Box>
                                        <Box sx={{
                                            flex: 1,
                                            p: 2,
                                            background: '#1e1e1e',
                                            fontFamily: 'monospace',
                                            fontSize: '14px',
                                            lineHeight: 1.8,
                                            overflow: 'auto'
                                        }}>
                                            <div style={{
                                                color: '#2e7d32',
                                                marginBottom: '8px',
                                                fontWeight: 'bold'
                                            }}>Modified:
                                            </div>
                                            {selectedFormula.BasicMathFormula?.split(/(\{[^}]+\})/).map((part: string, i: number) => (
                                                part.startsWith('{') && part.endsWith('}') ? (
                                                    <span key={i} style={{
                                                        color: '#9cdcfe',
                                                        background: '#2d2d2d',
                                                        padding: '2px 6px',
                                                        margin: '0 2px'
                                                    }}>
                                                        {part}
                                                    </span>
                                                ) : (
                                                    <span key={i} style={{color: '#d4d4d4'}}>{part}</span>
                                                )
                                            ))}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{
                                        p: 2,
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffc107',
                                    }}>
                                        <Typography variant="body2" sx={{color: '#856404', mb: 2}}>
                                            This formula is not available in the comparison version.
                                        </Typography>
                                        <Box sx={{
                                            p: 2,
                                            background: '#1e1e1e',
                                            fontFamily: 'monospace',
                                            fontSize: '14px',
                                            lineHeight: 1.8
                                        }}>
                                            {selectedFormula.BasicMathFormula?.split(/(\{[^}]+\})/).map((part: string, i: number) => (
                                                part.startsWith('{') && part.endsWith('}') ? (
                                                    <span key={i} style={{
                                                        color: '#9cdcfe',
                                                        background: '#2d2d2d',
                                                        padding: '2px 6px',
                                                        margin: '0 2px'
                                                    }}>
                                                        {part}
                                                    </span>
                                                ) : (
                                                    <span key={i} style={{color: '#d4d4d4'}}>{part}</span>
                                                )
                                            ))}
                                        </Box>
                                    </Box>
                                )
                            ) : (
                                <Box sx={{
                                    p: 2,
                                    background: '#1e1e1e',
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    lineHeight: 1.8
                                }}>
                                    {selectedFormula.BasicMathFormula?.split(/(\{[^}]+\})/).map((part: string, i: number) => (
                                        part.startsWith('{') && part.endsWith('}') ? (
                                            <span key={i} style={{
                                                color: '#9cdcfe',
                                                background: '#2d2d2d',
                                                padding: '2px 6px',
                                                margin: '0 2px'
                                            }}>
                                                {part}
                                            </span>
                                        ) : (
                                            <span key={i} style={{color: '#d4d4d4'}}>{part}</span>
                                        )
                                    ))}
                                </Box>
                            )
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    )
}

interface VersionFileViewerProps {
    version: Version
    compareVersion?: Version | null
    diffMode?: boolean
    setDiffMode?: (value: boolean) => void
    loadingVersions?: boolean
    availableStates?: string[]
    selectedCompareState?: string
    setSelectedCompareState?: (value: string) => void
    versionsForSelectedState?: Version[]
    setCompareVersion?: (version: Version | null) => void
    previousVersion?: Version | null
    loadingPreviousVersion?: boolean
}

function VersionFileViewer({
                               version,
                               compareVersion,
                               diffMode = false,
                               setDiffMode,
                               loadingVersions = false,
                               availableStates = [],
                               selectedCompareState = '',
                               setSelectedCompareState,
                               versionsForSelectedState = [],
                               setCompareVersion,
                               previousVersion = null,
                               loadingPreviousVersion = false
                           }: VersionFileViewerProps) {
    const [selectedFileView, setSelectedFileView] = useState<string>('changes-summary')
    const [fileContent, setFileContent] = useState<any>(null)
    const [compareFileContent, setCompareFileContent] = useState<any>(null)
    const [loadingFile, setLoadingFile] = useState(false)
    const [loadingCompareFile, setLoadingCompareFile] = useState(false)

    // Separate state for changes summary data (cached and not overwritten by file navigation)
    const [changesSummaryFormulaData, setChangesSummaryFormulaData] = useState<any>(null)
    const [changesSummaryCompareFormulaData, setChangesSummaryCompareFormulaData] = useState<any>(null)
    const [changesSummaryRateTableData, setChangesSummaryRateTableData] = useState<any>(null)
    const [changesSummaryCompareRateTableData, setChangesSummaryCompareRateTableData] = useState<any>(null)
    const [changesSummaryRateTableToFileMap, setChangesSummaryRateTableToFileMap] = useState<Map<string, string>>(new Map())
    const [changesSummaryCompareRateTableToFileMap, setChangesSummaryCompareRateTableToFileMap] = useState<Map<string, string>>(new Map())
    const [changesSummaryVersionKeysData, setChangesSummaryVersionKeysData] = useState<string[] | null>(null)
    const [changesSummaryCompareVersionKeysData, setChangesSummaryCompareVersionKeysData] = useState<string[] | null>(null)
    const [loadingChangesSummary, setLoadingChangesSummary] = useState(false)
    // Error states for each vertical
    const [formulasLoadError, setFormulasLoadError] = useState<string | null>(null)
    const [versionKeysLoadError, setVersionKeysLoadError] = useState<string | null>(null)
    const [rateTablesLoadError, setRateTablesLoadError] = useState<string | null>(null)
    const loadedCompareVersionRef = useRef<string | null>(null)

    const [selectedFormulaDomain, setSelectedFormulaDomain] = useState<string>('')
    const [selectedFormulaCoverage, setSelectedFormulaCoverage] = useState<string>('')
    const [selectedFormulaKey, setSelectedFormulaKey] = useState<string>('')
    const [selectedRateTable, setSelectedRateTable] = useState<string>('')
    const [activeCompareVersion, setActiveCompareVersion] = useState<Version | null>(null)
    const [formulaTabIndex, setFormulaTabIndex] = useState(0)
    const [hoveredVersionKey, setHoveredVersionKey] = useState<string | null>(null)
    const [pinnedVersionKey, setPinnedVersionKey] = useState<string | null>(null)

    // Changes Summary state persistence
    const [collapsedDomains, setCollapsedDomains] = useState<Set<string>>(new Set())
    const [collapsedCoverages, setCollapsedCoverages] = useState<Set<string>>(new Set())
    const [versionKeysFilter, setVersionKeysFilter] = useState<Set<string>>(new Set())
    const [formulasFilter, setFormulasFilter] = useState<Set<string>>(new Set())
    const [rateTablesFilter, setRateTablesFilter] = useState<Set<string>>(new Set())

    const handleFileChange = async (value: string) => {
        setSelectedFileView(value)

        // Reset formula selection when changing away from formulas.json
        if (value !== 'formulas.json') {
            setSelectedFormulaDomain('')
            setSelectedFormulaCoverage('')
            setSelectedFormulaKey('')
        }

        // Reset rate table selection when changing away from rate table files
        if (!value.toLowerCase().includes('ratetable')) {
            setSelectedRateTable('')
        }

        if (value !== 'changes-summary') {
            setLoadingFile(true)
            try {
                const jsonContent = await fetchVersionFileAsJson(version.versionId, value)
                setFileContent(jsonContent)
            } catch (error) {
                console.error('Failed to fetch file:', error)
                setFileContent(null)
            } finally {
                setLoadingFile(false)
            }
        }
        // Don't clear fileContent when navigating to changes-summary
        // The changes summary effect will have already loaded allFormulas.json into fileContent
    }

    const handleNavigateToFormula = (formulaPath: string) => {
        // Parse the path: "domain/coverage/formula"
        const [domain, coverage, formula] = formulaPath.split('/')

        // Enable diff mode if not already enabled, and set to previous version
        // If diff mode is already active, preserve the user's current selection
        if (setDiffMode && !diffMode && setCompareVersion && previousVersion && setSelectedCompareState) {
            setDiffMode(true)
            setCompareVersion(previousVersion)
            setSelectedCompareState(previousVersion.state)
        }

        // Navigate to formulas.json
        setSelectedFileView('formulas.json')

        // Set the selected formula
        setSelectedFormulaDomain(domain)
        setSelectedFormulaCoverage(coverage)
        setSelectedFormulaKey(formula)

        // Set tab to Expression if available (ColumnType 'E' or 'Q')
        // Use cached changes summary data to determine tab
        const formulaData = changesSummaryFormulaData?.formula?.[domain]?.[coverage]?.[formula]
        const columnType = formulaData?.ColumnType
        setFormulaTabIndex((columnType === 'E' || columnType === 'Q') ? 1 : 0)
    }

    const handleNavigateToRateTable = (tableName: string) => {
        // Find which file contains this table from the cached changes summary data
        // Check current version first, then comparison version (for removed tables)
        let fileName = changesSummaryRateTableToFileMap.get(tableName)
        if (!fileName) {
            fileName = changesSummaryCompareRateTableToFileMap.get(tableName)
        }

        if (!fileName) {
            console.error(`Could not find file for rate table: ${tableName}`)
            return
        }

        // Enable diff mode if not already enabled, and set to previous version
        // If diff mode is already active, preserve the user's current selection
        if (setDiffMode && !diffMode && setCompareVersion && previousVersion && setSelectedCompareState) {
            setDiffMode(true)
            setCompareVersion(previousVersion)
            setSelectedCompareState(previousVersion.state)
        }

        // Navigate to the rate table file
        setSelectedFileView(fileName)

        // Set the selected table
        setSelectedRateTable(tableName)
    }

    const isAllFormulas = selectedFileView === 'formulas.json'
    const formulasData = isAllFormulas && fileContent?.formula ? fileContent.formula : null
    const compareFormulasData = isAllFormulas && compareFileContent?.formula ? compareFileContent.formula : null

    // Check if the selected file is a rate tables file by name or structure
    const isRateTablesByName = selectedFileView?.toLowerCase().includes('ratetable')
    const isRateTablesByStructure = fileContent && typeof fileContent === 'object' && !Array.isArray(fileContent) &&
        Object.values(fileContent).some((val: any) => val?.headers && val?.rows)
    const isRateTables = isRateTablesByName || isRateTablesByStructure
    const rateTablesData = isRateTables ? fileContent : null
    const compareRateTablesData = isRateTables && compareFileContent ? compareFileContent : null
    const isDiffMode = !!compareVersion

    // Refetch main file when version changes (but keep the same file selected)
    useEffect(() => {
        if (selectedFileView !== 'changes-summary') {
            setLoadingFile(true)
            fetchVersionFileAsJson(version.versionId, selectedFileView)
                .then((jsonContent) => {
                    setFileContent(jsonContent)
                })
                .catch((error) => {
                    console.error('Failed to fetch file:', error)
                    setFileContent(null)
                })
                .finally(() => {
                    setLoadingFile(false)
                })
        }
    }, [version.versionId, selectedFileView])

    // Load changes summary data on mount or when compareVersion changes
    useEffect(() => {
        // Determine which version to compare against
        const versionToLoad = compareVersion || previousVersion

        if (!versionToLoad) {
            setLoadingChangesSummary(false)
            setActiveCompareVersion(null)
            loadedCompareVersionRef.current = null
            return
        }

        // Skip if we've already loaded data for this compare version
        if (loadedCompareVersionRef.current === versionToLoad.versionId) {
            setActiveCompareVersion(versionToLoad)
            return
        }

        // Load the data for this compare version
        setActiveCompareVersion(versionToLoad)
        loadedCompareVersionRef.current = versionToLoad.versionId
        setLoadingChangesSummary(true)

        const fetchData = async () => {
            // Reset error states
            setFormulasLoadError(null)
            setVersionKeysLoadError(null)
            setRateTablesLoadError(null)

            // Fetch all verticals in parallel, each with independent error handling
            const fetchFormulas = async () => {
                try {
                    const jsonContent = await fetchVersionFileAsJson(version.versionId, 'formulas.json')
                    setChangesSummaryFormulaData(jsonContent)

                    const prevJsonContent = await fetchVersionFileAsJson(versionToLoad.versionId, 'formulas.json')
                    setChangesSummaryCompareFormulaData(prevJsonContent)
                } catch (err: any) {
                    console.error('Failed to fetch formulas:', err)
                    setFormulasLoadError(err?.message || 'Failed to load formulas')
                    setChangesSummaryFormulaData(null)
                    setChangesSummaryCompareFormulaData(null)
                }
            }

            const fetchVersionKeys = async () => {
                try {
                    const vkContent = await fetchVersionFileAsJson(version.versionId, 'versionKeys.json')
                    const vkArray = typeof vkContent === 'object' && vkContent !== null && !Array.isArray(vkContent)
                        ? Object.keys(vkContent)
                        : []
                    setChangesSummaryVersionKeysData(vkArray)

                    const compareVkContent = await fetchVersionFileAsJson(versionToLoad.versionId, 'versionKeys.json')
                    const compareVkArray = typeof compareVkContent === 'object' && compareVkContent !== null && !Array.isArray(compareVkContent)
                        ? Object.keys(compareVkContent)
                        : []
                    setChangesSummaryCompareVersionKeysData(compareVkArray)
                } catch (err: any) {
                    console.error('Failed to fetch versionKeys:', err)
                    setVersionKeysLoadError(err?.message || 'Failed to load version keys')
                    setChangesSummaryVersionKeysData(null)
                    setChangesSummaryCompareVersionKeysData(null)
                }
            }

            const fetchRateTables = async () => {
                try {
                    const currentRateTableFiles = version.versionFiles
                        ? Object.keys(version.versionFiles).filter(f => f.toLowerCase().includes('ratetable'))
                        : []

                    const compareRateTableFiles = versionToLoad.versionFiles
                        ? Object.keys(versionToLoad.versionFiles).filter(f => f.toLowerCase().includes('ratetable'))
                        : []

                    // Fetch all current rate table files and merge them
                    let mergedCurrentRateTables: any = {}
                    const tableToFileMapping = new Map<string, string>()
                    for (const file of currentRateTableFiles) {
                        const rtContent = await fetchVersionFileAsJson(version.versionId, file)
                        Object.keys(rtContent).forEach(tableName => {
                            if (rtContent[tableName]?.headers && rtContent[tableName]?.rows) {
                                tableToFileMapping.set(tableName, file)
                            }
                        })
                        mergedCurrentRateTables = {...mergedCurrentRateTables, ...rtContent}
                    }
                    setChangesSummaryRateTableData(mergedCurrentRateTables)
                    setChangesSummaryRateTableToFileMap(tableToFileMapping)

                    // Fetch all comparison rate table files and merge them
                    let mergedCompareRateTables: any = {}
                    const compareTableToFileMapping = new Map<string, string>()
                    for (const file of compareRateTableFiles) {
                        const rtContent = await fetchVersionFileAsJson(versionToLoad.versionId, file)
                        Object.keys(rtContent).forEach(tableName => {
                            if (rtContent[tableName]?.headers && rtContent[tableName]?.rows) {
                                compareTableToFileMapping.set(tableName, file)
                            }
                        })
                        mergedCompareRateTables = {...mergedCompareRateTables, ...rtContent}
                    }
                    setChangesSummaryCompareRateTableData(mergedCompareRateTables)
                    setChangesSummaryCompareRateTableToFileMap(compareTableToFileMapping)
                } catch (err: any) {
                    console.error('Failed to fetch rate tables:', err)
                    setRateTablesLoadError(err?.message || 'Failed to load rate tables')
                    setChangesSummaryRateTableData(null)
                    setChangesSummaryCompareRateTableData(null)
                }
            }

            // Run all fetches in parallel
            await Promise.all([fetchFormulas(), fetchVersionKeys(), fetchRateTables()])
            setLoadingChangesSummary(false)
        }
        fetchData()
    }, [previousVersion, compareVersion, version.versionId])

    // Refetch comparison file when compareVersion changes
    useEffect(() => {
        if (!compareVersion || selectedFileView === 'changes-summary') {
            setCompareFileContent(null)
            return
        }

        const fetchCompareFile = async () => {
            setLoadingCompareFile(true)
            try {
                const jsonContent = await fetchVersionFileAsJson(compareVersion.versionId, selectedFileView)
                setCompareFileContent(jsonContent)
            } catch (error) {
                console.error('Failed to fetch comparison file:', error)
                setCompareFileContent(null)
            } finally {
                setLoadingCompareFile(false)
            }
        }

        fetchCompareFile()
    }, [compareVersion, selectedFileView])

    const fileNames = version.versionFiles ? Object.keys(version.versionFiles) : []
    const isChangesSummary = selectedFileView === 'changes-summary'

    return (
        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box sx={{display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                backgroundColor: '#e5edf1',
                // border: '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '8px 10px'}}>
                <Tooltip title="Changes Summary">
                    <IconButton
                        onClick={() => handleFileChange('changes-summary')}
                        size="small"
                        sx={{
                            backgroundColor: isChangesSummary ? '#1976d2' : 'white',
                            color: isChangesSummary ? 'white' : '#666',
                            '&:hover': {
                                backgroundColor: isChangesSummary ? '#1565c0' : '#f5f5f5'
                            },
                            border: '1px solid',
                            borderColor: isChangesSummary ? '#1976d2' : '#1976d2'
                        }}
                    >
                        <CompareArrowsIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
                <Select
                    value={selectedFileView}
                    onChange={(e) => handleFileChange(e.target.value)}
                    size="sm"
                    sx={{minWidth: 200}}
                >
                    <MenuItem value="changes-summary">Change Summary</MenuItem>
                    {fileNames.map((fileName) => {
                        const displayName = fileName === 'rateTables.json' ? 'Rate Tables'
                            : fileName === 'formulas.json' ? 'Formulas'
                            : fileName === 'versionKeys.json' ? 'VersionKeys JSON'
                            : fileName
                        return <MenuItem key={fileName} value={fileName}>{displayName}</MenuItem>
                    })}
                </Select>

                {setDiffMode && (
                    <>
                        <span style={{marginLeft: '10px', borderLeft: '1px solid #ccc', paddingLeft: '20px'}}>
                            <Button
                                variant={diffMode ? 'contained' : 'contained'}
                                style={{backgroundColor: diffMode ? '#1976d2' : 'white', color: diffMode ? 'white' : '#666'}}
                                onClick={() => setDiffMode(!diffMode)}
                                size="sm"
                            >
                                {diffMode ? 'Exit Diff Mode' : 'Compare Versions'}
                            </Button>
                        </span>
                    </>
                )}

                {diffMode && setSelectedCompareState && setCompareVersion && (
                    <>
                        <span style={{fontWeight: 600}}>Compare with:</span>
                        <Select
                            value={selectedCompareState}
                            onChange={(e) => {
                                setSelectedCompareState(e.target.value)
                                setCompareVersion(null)
                            }}
                            size="sm"
                            sx={{minWidth: 120}}
                            disabled={loadingVersions}
                        >
                            <MenuItem value="">
                                {loadingVersions ? 'Loading...' : 'Select state'}
                            </MenuItem>
                            {availableStates.map((state) => (
                                <MenuItem key={state} value={state}>
                                    {state}
                                </MenuItem>
                            ))}
                        </Select>
                        {selectedCompareState && (
                            <Select
                                value={compareVersion?.versionId || ''}
                                onChange={(e) => {
                                    const selected = versionsForSelectedState.find(v => v.versionId === e.target.value)
                                    setCompareVersion(selected || null)
                                }}
                                size="sm"
                                sx={{minWidth: 250}}
                            >
                                <MenuItem value="">Select version</MenuItem>
                                {versionsForSelectedState.map((v) => (
                                    <MenuItem key={v.versionId} value={v.versionId}>
                                        {v.label || v.versionId} - {formatEffectiveDate(v.effectiveDate)}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                        {activeCompareVersion && (
                            <span style={{fontSize: '12px', color: '#666', marginLeft: 'auto'}}>
                                Comparing {version.state} {version.label || version.versionId} with {activeCompareVersion.state} {activeCompareVersion.label || activeCompareVersion.versionId}
                            </span>
                        )}
                    </>
                )}

                {!diffMode && selectedFileView === 'changes-summary' && activeCompareVersion && (
                    <span style={{fontSize: '12px', color: '#666', marginLeft: 'auto'}}>
                        Comparing {version.state} {version.label || version.versionId} with {activeCompareVersion.state} {activeCompareVersion.label || activeCompareVersion.versionId}
                    </span>
                )}
            </Box>
            <div style={{
                fontSize: '14px',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'auto'
            }}>
                {selectedFileView === 'changes-summary' ? (
                    loadingPreviousVersion || loadingChangesSummary ? (
                        <Box sx={{p: 3}}>
                            <Typography>Loading changes...</Typography>
                        </Box>
                    ) : (
                        <VersionChangeSummary
                            version={version}
                            previousVersion={previousVersion}
                            compareVersion={activeCompareVersion}
                            versionFileContent={changesSummaryFormulaData}
                            previousVersionFileContent={changesSummaryCompareFormulaData}
                            rateTableContent={changesSummaryRateTableData}
                            previousRateTableContent={changesSummaryCompareRateTableData}
                            versionKeysContent={changesSummaryVersionKeysData}
                            compareVersionKeysContent={changesSummaryCompareVersionKeysData}
                            formulasLoadError={formulasLoadError}
                            versionKeysLoadError={versionKeysLoadError}
                            rateTablesLoadError={rateTablesLoadError}
                            onNavigateToFormula={handleNavigateToFormula}
                            onNavigateToRateTable={handleNavigateToRateTable}
                            hoveredVersionKey={hoveredVersionKey}
                            onVersionKeyHover={setHoveredVersionKey}
                            pinnedVersionKey={pinnedVersionKey}
                            onVersionKeyTogglePin={(key) => setPinnedVersionKey(pinnedVersionKey === key ? null : key)}
                            collapsedDomains={collapsedDomains}
                            setCollapsedDomains={setCollapsedDomains}
                            collapsedCoverages={collapsedCoverages}
                            setCollapsedCoverages={setCollapsedCoverages}
                            versionKeysFilter={versionKeysFilter}
                            setVersionKeysFilter={setVersionKeysFilter}
                            formulasFilter={formulasFilter}
                            setFormulasFilter={setFormulasFilter}
                            rateTablesFilter={rateTablesFilter}
                            setRateTablesFilter={setRateTablesFilter}
                        />
                    )
                ) : loadingFile || loadingCompareFile ? (
                    <div>Loading {loadingCompareFile ? 'comparison ' : ''}file...</div>
                ) : isAllFormulas && formulasData ? (
                    isDiffMode && compareFormulasData ? (
                        <div key={`formula-viewer-${selectedFileView}`} style={{
                            marginTop: '15px',
                            borderTop: '1px solid #ccc',
                            paddingTop: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0
                        }}>
                            <FormulaViewer
                                formulas={formulasData}
                                compareFormulas={compareFormulasData}
                                selectedDomain={selectedFormulaDomain}
                                setSelectedDomain={setSelectedFormulaDomain}
                                selectedCoverage={selectedFormulaCoverage}
                                setSelectedCoverage={setSelectedFormulaCoverage}
                                selectedFormulaKey={selectedFormulaKey}
                                setSelectedFormulaKey={setSelectedFormulaKey}
                                formulaTabIndex={formulaTabIndex}
                                setFormulaTabIndex={setFormulaTabIndex}
                            />
                        </div>
                    ) : (
                        <div key={`formula-viewer-${selectedFileView}`} style={{
                            marginTop: '15px',
                            borderTop: '1px solid #ccc',
                            paddingTop: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0
                        }}>
                            <FormulaViewer
                                formulas={formulasData}
                                selectedDomain={selectedFormulaDomain}
                                setSelectedDomain={setSelectedFormulaDomain}
                                selectedCoverage={selectedFormulaCoverage}
                                setSelectedCoverage={setSelectedFormulaCoverage}
                                selectedFormulaKey={selectedFormulaKey}
                                setSelectedFormulaKey={setSelectedFormulaKey}
                                formulaTabIndex={formulaTabIndex}
                                setFormulaTabIndex={setFormulaTabIndex}
                            />
                        </div>
                    )
                ) : isRateTables && rateTablesData ? (
                    isDiffMode && compareRateTablesData ? (
                        <RateTableViewer
                            rateTables={rateTablesData}
                            compareRateTables={compareRateTablesData}
                            selectedTable={selectedRateTable}
                            setSelectedTable={setSelectedRateTable}
                        />
                    ) : (
                        <RateTableViewer
                            rateTables={rateTablesData}
                            selectedTable={selectedRateTable}
                            setSelectedTable={setSelectedRateTable}
                        />
                    )
                ) : isDiffMode && compareFileContent ? (
                    <DiffEditor
                        key={`diff-${selectedFileView}-${version.versionId}-${compareVersion?.versionId || 'none'}`}
                        original={compareFileContent}
                        modified={fileContent}
                    />
                ) : (
                    <JsonView data={fileContent} placeholder="No file content"/>
                )}
            </div>
        </div>
    )
}

export default VersionFileViewer
